/**
 * The Ctrl+K palette's plain parts (docs/refactor/07-ctrl-k.md): how a typed text matches an entry,
 * how the entries are ranked and grouped, and which were picked lately. The component that draws
 * them is components/CommandPalette.tsx; everything here can be tested without a screen.
 *
 * No search library: a substring match beats a scattered one, the start of a word beats the middle,
 * and among equals what was picked lately comes first.
 */

/** The sections, in the order they are shown. */
export const PALETTE_SECTIONS = ['recent', 'go', 'projects', 'files', 'docs', 'sessions', 'tasks', 'actions'] as const
export type PaletteSection = (typeof PALETTE_SECTIONS)[number]

export interface PaletteEntry {
  /** Stable across openings: it is what "recent" remembers. */
  id: string
  section: Exclude<PaletteSection, 'recent'>
  label: string
  /** A second, quieter line: the path, the project, the area. */
  hint?: string
  /** More words it is found by, never shown. */
  keywords?: string
  run: () => void | Promise<void>
}

/** Lower case, accents removed: "Início" is found by typing "inicio". */
export function normalize(text: string): string {
  return text.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
}

/**
 * How well `query` matches `text`: null when it does not, higher when it matches better.
 * A whole substring scores above any scattered match; at the start, or at a word's start, scores more.
 */
export function matchScore(query: string, text: string): number | null {
  const q = normalize(query.trim())
  if (!q) return 0
  const t = normalize(text)
  const at = t.indexOf(q)
  if (at >= 0) {
    const wordStart = at === 0 || /[\s/._\-›·]/.test(t[at - 1]!)
    return 1000 - Math.min(at, 200) + (at === 0 ? 300 : 0) + (wordStart ? 150 : 0) - Math.min(t.length, 200) / 10
  }
  // Scattered: every typed letter, in order ("dcs" finds "docs"); fewer gaps scores more.
  let from = 0
  let gaps = 0
  for (const char of q) {
    if (char === ' ') continue
    const found = t.indexOf(char, from)
    if (found < 0) return null
    gaps += found - from
    from = found + 1
  }
  return Math.max(1, 400 - gaps * 4 - Math.min(t.length, 200) / 10)
}

function entryScore(query: string, entry: PaletteEntry): number | null {
  const scores = [matchScore(query, entry.label), entry.hint ? matchScore(query, entry.hint) : null, entry.keywords ? matchScore(query, entry.keywords) : null]
  // The label counts most: a match only in the path or the hidden words ranks below one in the name.
  const [label, hint, keywords] = scores
  const best = Math.max(label ?? -Infinity, (hint ?? -Infinity) - 200, (keywords ?? -Infinity) - 100)
  return best === -Infinity ? null : best
}

export interface PaletteGroup {
  section: PaletteSection
  entries: PaletteEntry[]
}

/** How many entries each section shows: all of the screens, a handful of everything else. */
const LIMITS: Record<PaletteSection, number> = { recent: 5, go: 40, projects: 8, files: 20, docs: 8, sessions: 6, tasks: 6, actions: 20 }

/**
 * The entries that match, best first, grouped by section in the fixed order. With nothing typed,
 * the recently picked come first, in their own section; files only show once something is typed.
 */
export function rankEntries(entries: PaletteEntry[], query: string, recent: string[]): PaletteGroup[] {
  // "/" first: only the prompt box's commands, as in the box itself ("/te" → /testar).
  // Ranked by the command's own name, those starting with what was typed first.
  if (query.trimStart().startsWith('/')) {
    const typedName = normalize(query.trimStart().slice(1).trim())
    const named = entries
      .filter((entry) => entry.id.startsWith('command:'))
      .map((entry) => ({ entry, name: entry.id.slice('command:'.length) }))
      .filter(({ name }) => name.includes(typedName))
      .sort((a, b) => Number(b.name.startsWith(typedName)) - Number(a.name.startsWith(typedName)))
      .map(({ entry }) => entry)
    return named.length ? [{ section: 'actions', entries: named.slice(0, LIMITS.actions) }] : []
  }
  const typed = query.trim().length > 0
  const recency = new Map(recent.map((id, index) => [id, index]))
  const scored = entries
    .map((entry, index) => ({ entry, index, score: entryScore(query, entry) }))
    .filter((item): item is { entry: PaletteEntry; index: number; score: number } => item.score !== null)
    .sort((a, b) =>
      b.score - a.score
      || (recency.get(a.entry.id) ?? Infinity) - (recency.get(b.entry.id) ?? Infinity)
      || a.index - b.index)

  const groups: PaletteGroup[] = []
  const shown = new Set<string>()
  if (!typed) {
    const byId = new Map(entries.map((entry) => [entry.id, entry]))
    const recentEntries = recent.map((id) => byId.get(id)).filter((entry): entry is PaletteEntry => Boolean(entry)).slice(0, LIMITS.recent)
    if (recentEntries.length) groups.push({ section: 'recent', entries: recentEntries })
    for (const entry of recentEntries) shown.add(entry.id)
  }
  for (const section of PALETTE_SECTIONS) {
    if (section === 'recent') continue
    if (section === 'files' && !typed) continue
    const inSection = scored.filter((item) => item.entry.section === section && !shown.has(item.entry.id)).slice(0, LIMITS[section])
    if (inSection.length) groups.push({ section, entries: inSection.map((item) => item.entry) })
  }
  return groups
}

// ------------------------------------------------------------------------------ recent

const RECENT_KEY = 'devpulse.palette.recent'
const RECENT_MAX = 12

export function recentIds(): string[] {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(RECENT_KEY) ?? '[]')
    return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string') : []
  } catch {
    return []
  }
}

export function rememberRecent(id: string): void {
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify([id, ...recentIds().filter((x) => x !== id)].slice(0, RECENT_MAX)))
  } catch {
    // Storage blocked: nothing is remembered, the palette still works.
  }
}

// ------------------------------------------------------------------------------ opening it

/** Sent on window by anything that opens the palette (the header's search button). */
export const PALETTE_EVENT = 'devpulse:palette-open'

export function openPalette(): void {
  window.dispatchEvent(new CustomEvent(PALETTE_EVENT))
}

/** Ctrl+K, or ⌘K on a Mac. */
export function isPaletteShortcut(event: Pick<KeyboardEvent, 'key' | 'ctrlKey' | 'metaKey' | 'altKey' | 'shiftKey'>): boolean {
  return (event.ctrlKey || event.metaKey) && !event.altKey && !event.shiftKey && event.key.toLowerCase() === 'k'
}
